import type { DateTime } from "luxon";
import {
	AutoPostRationaleKind,
	type ExternalSource,
	type HppdRationale,
	type Nurse,
	type OriginPerson,
	type RatioRationale,
	RequestOriginKind,
	RequestResponsesStatusOptions,
	type ShiftRequest,
	type TimeOffDays,
} from "./models";
import { requestTakesAnyRole } from "./roles";
import { deterministicPercent, hashString } from "./utilFunctions";

/**
 * Demo-only seeding primitives for `demoRequestOrigins.ts`: deterministic picks keyed on the request id, so the same
 * request always tells the same story. Deleted with the rest of the demo origins when the real resolver lands.
 */
export type DemoOriginsContext = {
	/** Schedulable nurses of the department, sorted by id so a seeded pick never depends on load order. */
	roster: readonly Nurse[];
	/** The signed-in user, credited only for what their own user id created. */
	currentUser: { userId: string; person: OriginPerson } | null;
	/** The department's time-off HR system, when it has one. */
	hrisSource: ExternalSource | null;
	/** Locations with a ratio ladder; empty in a department that does not staff by ratio. */
	ratioLocations: readonly string[];
	timezone: string;
	now: DateTime;
	nameOf: (nurse: Nurse) => string;
};

export const PERCENT_SCALE = 100;
/** A seeded yes/no splits the requests evenly. */
export const COIN_FLIP_PERCENT = 50;

/**
 * The cause a posted request is given when its real fields do not name one: auto-posts lead, the way a demo
 * department's list reads once InHouse is doing the posting. Weights add up to `PERCENT_SCALE`.
 */
export const SEEDED_KIND_WEIGHTS: readonly (readonly [RequestOriginKind, number])[] = [
	[RequestOriginKind.AutoPost, 50],
	[RequestOriginKind.PostedByPerson, 12],
	[RequestOriginKind.TimeOff, 8],
	[RequestOriginKind.TimeOffHris, 8],
	[RequestOriginKind.PostedByAnnie, 8],
	[RequestOriginKind.Deactivation, 7],
	[RequestOriginKind.FromRecommendation, 7],
];

export const percent = (request: ShiftRequest, salt: string): number => deterministicPercent(`${request.id}:${salt}`);

export const between = (request: ShiftRequest, salt: string, min: number, max: number): number => min + (percent(request, salt) / PERCENT_SCALE) * (max - min);

export const seededIndex = (length: number, seed: string): number => Math.abs(hashString(seed)) % length;

export const pick = <T>(items: readonly T[], seed: string): T | null => (items.length === 0 ? null : items[seededIndex(items.length, seed)]);

export const weightedKind = (request: ShiftRequest, salt: string): RequestOriginKind => {
	const value = percent(request, salt);
	let ceiling = 0;
	for (const [kind, weight] of SEEDED_KIND_WEIGHTS) {
		ceiling += weight;
		if (value < ceiling) return kind;
	}
	throw new Error(`SEEDED_KIND_WEIGHTS must add up to ${PERCENT_SCALE}; ${value} fell past ${ceiling}`);
};

export const leadMinutesOf = (request: ShiftRequest, at: DateTime): number => Math.round(request.message.shift_start.diff(at, "minutes").minutes);

export const toPerson = (nurse: Nurse, ctx: DemoOriginsContext): OriginPerson => ({ nurseId: nurse.id, name: ctx.nameOf(nurse) });

export const agencyNameOf = (request: ShiftRequest): string | null =>
	"agency_source_name" in request && typeof request.agency_source_name === "string" && request.agency_source_name !== "" ? request.agency_source_name : null;

/** Slots filled so far: the approved answers. `needed_nurses` is what is still open, so the two add up to all opened. */
export const approvedCountOf = (request: ShiftRequest): number =>
	request.message.request_data.filter((answer) => answer.status === RequestResponsesStatusOptions.APPROVED).length;

const fitsRequest = (nurse: Nurse, request: ShiftRequest): boolean =>
	request.job_id !== undefined ? nurse.jobIds?.includes(request.job_id) === true : requestTakesAnyRole(request.message.roles, [nurse.role]);

export const pickRosterNurse = (request: ShiftRequest, salt: string, ctx: DemoOriginsContext, excludeId: number | null): Nurse | null => {
	const pool = ctx.roster.filter((nurse) => nurse.id !== excludeId);
	const fitting = pool.filter((nurse) => fitsRequest(nurse, request));
	return pick(fitting, `${request.id}:${salt}`);
};

export const rosterNurseById = (ctx: DemoOriginsContext, nurseId: number): Nurse | null => ctx.roster.find((nurse) => nurse.id === nurseId) ?? null;

/** Whoever the request's creator is, when the demo can say: a roster nurse, or the signed-in user if it was them. */
export const creatorOf = (request: ShiftRequest, ctx: DemoOriginsContext): OriginPerson | null => {
	const nurse = ctx.roster.find((candidate) => candidate.userId === request.created_by);
	if (nurse !== undefined) return toPerson(nurse, ctx);
	return ctx.currentUser !== null && ctx.currentUser.userId === request.created_by ? ctx.currentUser.person : null;
};

/** Who posted it. A post is a scheduler's act, so an unknown creator is told as the signed-in scheduler's. */
export const posterOf = (request: ShiftRequest, ctx: DemoOriginsContext): OriginPerson | null => {
	const creator = creatorOf(request, ctx);
	if (creator !== null) return creator;
	// DEFAULT: demo only — the seeder's requests carry a system creator; the signed-in scheduler stands in as poster.
	return ctx.currentUser === null ? null : ctx.currentUser.person;
};

const HPPD_CENSUS = { MIN: 55, MAX: 90 } as const;
const HPPD_TARGET = { MIN: 3.2, MAX: 3.6 } as const;
const HPPD_SHORTFALL = { MIN: 0.12, MAX: 0.4 } as const;
const HPPD_DECIMALS = 2;
const RATIO_REQUIRED = { MIN: 4, MAX: 7 } as const;
const RATIO_SCHEDULED_SHORT_BY = 2;
const RATIO_ALREADY_POSTED = 1;
const TIME_OFF_DAYS_BEFORE = { MIN: 0, MAX: 1 } as const;
const TIME_OFF_DAYS_AFTER = { MIN: 0, MAX: 2 } as const;

const seededInt = (request: ShiftRequest, salt: string, range: { MIN: number; MAX: number }): number =>
	range.MIN + Math.floor((percent(request, salt) / PERCENT_SCALE) * (range.MAX - range.MIN + 1));
const toDecimals = (value: number): number => Number(value.toFixed(HPPD_DECIMALS));

const hppdRationale = (request: ShiftRequest): HppdRationale => {
	const census = seededInt(request, "census", HPPD_CENSUS);
	const targetHppd = toDecimals(between(request, "target", HPPD_TARGET.MIN, HPPD_TARGET.MAX));
	const scheduledHppd = toDecimals(targetHppd - between(request, "gap", HPPD_SHORTFALL.MIN, HPPD_SHORTFALL.MAX));
	const scheduledHours = Math.round(scheduledHppd * census);
	return { kind: AutoPostRationaleKind.Hppd, targetHppd, scheduledHppd, census, scheduledHours, neededHours: Math.round(targetHppd * census) };
};

/** A ratio story names one more post, so it is only told for a cause that opened exactly one slot. */
const ratioRationale = (request: ShiftRequest, ctx: DemoOriginsContext): RatioRationale | null => {
	const location =
		request.location !== undefined && ctx.ratioLocations.includes(request.location)
			? request.location
			: pick(ctx.ratioLocations, `${request.id}:ratio-location`);
	if (location === null) return null;
	const required = seededInt(request, "required", RATIO_REQUIRED);
	const jobId = request.job_id !== undefined ? request.job_id : null;
	return {
		kind: AutoPostRationaleKind.Ratio,
		location,
		jobId,
		roles: jobId === null ? request.message.roles : [],
		required,
		scheduled: required - RATIO_SCHEDULED_SHORT_BY,
		alreadyPosted: RATIO_ALREADY_POSTED,
	};
};

export const autoPostRationale = (request: ShiftRequest, ctx: DemoOriginsContext, allowRatio: boolean): HppdRationale | RatioRationale => {
	const ratio = allowRatio && percent(request, "rationale") < COIN_FLIP_PERCENT ? ratioRationale(request, ctx) : null;
	return ratio !== null ? ratio : hppdRationale(request);
};

/** Days off that always include the shift's own day, the way a real time-off approval reopens it. */
export const timeOffDays = (request: ShiftRequest, ctx: DemoOriginsContext): TimeOffDays => {
	const shiftDay = request.message.shift_start.setZone(ctx.timezone).startOf("day");
	return {
		firstDay: shiftDay.minus({ days: seededInt(request, "pto-before", TIME_OFF_DAYS_BEFORE) }),
		lastDay: shiftDay.plus({ days: seededInt(request, "pto-after", TIME_OFF_DAYS_AFTER) }),
	};
};
